import { useEffect, useState } from "react";
import axios from "axios";
import { Repeat } from "lucide-react";
import { FadeUp } from "./Reveal";
import { IMAGES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Palette tints (DoodleDivider colors) laid transparently over the watermark.
const TONES = {
  yellow: { tint: "bg-yellow/[0.22]", accent: "text-tangerine" },
  tangerine: { tint: "bg-tangerine/10", accent: "text-tangerine" },
  cobalt: { tint: "bg-cobalt/10", accent: "text-cobalt" },
  grass: { tint: "bg-grass/10", accent: "text-grass" },
  blossom: { tint: "bg-blossom/10", accent: "text-blossom" },
};

/**
 * SustainingBand — compact one-line thank-you roll for monthly General Fund
 * donors. Names only, never amounts. Founder pick: variant C, palette tint
 * over the footer's watermark pattern (2026-09-06).
 */
export const SustainingBand = ({ donors = [], count, tone = "yellow", testid = "sustaining-band", id }) => {
  const t = TONES[tone] || TONES.yellow;
  const n = count ?? donors.length;
  return (
    <section id={id} data-testid={testid} data-tone={tone} className="relative overflow-hidden bg-sand text-ink">
      <div aria-hidden="true" className={`pointer-events-none absolute inset-0 ${t.tint}`} />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: `url(${IMAGES.pattern})`, backgroundSize: "540px", backgroundRepeat: "repeat" }}
      />
      <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-8 lg:py-20">
        <FadeUp>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.28em] text-olive">
                <Repeat className={`h-3.5 w-3.5 ${t.accent}`} strokeWidth={2} /> Sustaining donors
              </p>
              <h2 className="mt-3 font-serif text-3xl leading-tight">Thank you for showing up every month.</h2>
            </div>
            <p className="text-[13px] text-olive" data-testid="sustaining-band-count">
              <span className="font-serif text-3xl text-ink">{n}</span> monthly {n === 1 ? "donor" : "donors"}
            </p>
          </div>
        </FadeUp>
        <FadeUp delay={0.08}>
          <p className="mt-8 flex flex-wrap items-baseline gap-y-2 font-serif text-3xl leading-snug text-ink" data-testid="sustaining-band-names">
            {donors.map((d, i) => (
              <span key={`${d.name}-${i}`} className="inline-flex items-baseline whitespace-nowrap" title={d.since ? `since ${d.since}` : undefined}>
                {d.name}
                {i < donors.length - 1 && <span aria-hidden="true" className={`mx-3 ${t.accent}`}>·</span>}
              </span>
            ))}
          </p>
          <p className="mt-6 text-[13px] leading-relaxed text-olive/80">
            Names appear only with the donor's permission at checkout. Amounts are never shown.
          </p>
        </FadeUp>
      </div>
    </section>
  );
};

/** Live band: fetches the public roll; renders nothing until someone has opted in. */
export const SustainingBandLive = (props) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/sustaining-donors`).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data || data.donors.length === 0) return null;
  return <SustainingBand id="sustaining" donors={data.donors} count={data.count} {...props} />;
};
